import React, { useState, useEffect } from 'react';
import { useApp } from '../App';
import { fetchEvents, patchEvent } from '../lib/gcal';
import { parseFOHMeta } from '../api/rules';
import { fsLogChange, fsSet, fsGetCol, fsDelete } from '../lib/firebase';
import { getRoutines, legAdultForDay, resolvePickupAdult } from '../lib/pickups';
import { stripAssigneeTag, getEventAssignee, buildFOHDescription } from '../api/assignments';
import { scheduleBatchedPublish } from '../api/schedulePublish';
import { standardizeTitle } from './titleStandardize';
import { ReminderIcon, RecurringIcon } from '../components/ui/icons';

// ─── RECURRING & DEFAULTS ─────────────────────────────────────────────────────
// Standing arrangements — recurring calendar series and weekly pickup patterns
// — live here so the day-to-day Review & Assign view can stay focused on
// exceptions. Series assignment patches the recurring event's master
// (propagates to all non-exception instances); pickup weekly patterns are
// edited in Settings → Family Setup → Children (shown here read-only, with
// unset defaults flagged so the standing gap — not just the day — gets fixed).

const WINDOW_DAYS = 60; // wide enough to reliably surface weekly/biweekly series

export default function RecurringDefaults({ onBack, embedded = false, onNavigateToChildren = null }) {
  const { familyData, setFamilyData, currentUser } = useApp() || {};
  const [series, setSeries]   = useState([]);
  const [ignoredList, setIgnoredList] = useState([]); // all locally-hidden events/series (any calendar, any window)
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [showCleanupPreview, setShowCleanupPreview] = useState(false);
  const [cleanupResults, setCleanupResults] = useState(null); // per-item [{ title, newTitle, status, reason }]
  const [applyingCleanup, setApplyingCleanup] = useState(false);
  const [actionError, setActionError] = useState(null);

  const carePartners  = familyData?.carePartners || [];
  const allSitters     = familyData?.sitters || [];
  const familySitters = allSitters.filter(s => s.sitterType === 'family' && s.active !== false);
  const children       = familyData?.children || [];
  const calAssignments = familyData?.config?.calendarAssignments || {};
  const suppressedCals = familyData?.config?.suppressedCalendars || [];
  const calendarMeta   = familyData?.config?.calendarMeta || [];
  // calId → { name, accessRole } — accessRole is authoritative read-only detection;
  // absent on families that haven't re-saved Settings → Calendars since it shipped.
  const calMetaById = calendarMeta.reduce((acc, m) => { acc[m.id] = m; return acc; }, {});

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyData?.id]);

  // Read-only = the family can't write to this calendar (subscribed/school/
  // shared-read). accessRole from calendarMeta is authoritative; the
  // organizer.self heuristic is the fallback for pre-accessRole config.
  function isReadOnlyCalendar(calId, sampleEvent) {
    const role = calMetaById[calId]?.accessRole;
    if (role) return role === 'reader' || role === 'freeBusyReader';
    return sampleEvent?.organizer?.self === false;
  }

  async function load() {
    if (!familyData) return;
    setLoading(true);
    setError(null);
    try {
      const calIds = Object.keys(calAssignments).filter(id => !suppressedCals.includes(id));
      const start = new Date(); start.setHours(0, 0, 0, 0);
      const end   = new Date(start); end.setDate(end.getDate() + WINDOW_DAYS); end.setHours(23, 59, 59, 999);

      const all = [];
      for (const calId of calIds) {
        try {
          const evts = await fetchEvents({ calendarId: calId, timeMin: start.toISOString(), timeMax: end.toISOString(), maxResults: 250 });
          for (const e of (evts || [])) if (e.recurringEventId) all.push({ ...e, _calendarId: calId });
        } catch (err) {
          console.warn('[RecurringDefaults] cal fetch failed:', calId, err.message);
        }
      }

      // Locally-hidden events/series — used both to filter the list below and
      // to populate the Ignore manager (kept in full, across all calendars and
      // outside this 60-day window, so anything hidden long ago stays visible).
      let ignoredMap = {};
      if (familyData?.id) {
        try {
          const ignored = await fsGetCol(`families/${familyData.id}/ignoredEvents`);
          for (const i of (ignored || [])) ignoredMap[i.id] = i;
          setIgnoredList(ignored || []);
        } catch (err) {
          console.warn('[RecurringDefaults] ignored events fetch failed:', err.message);
        }
      }

      // Group instances by their series master id — fetchEvents uses
      // singleEvents:true, so the master itself is never returned; the
      // earliest instance in-window stands in for the series (instances
      // inherit the master's description/title unless individually excepted).
      const byMaster = {};
      for (const e of all) {
        if (ignoredMap[e.recurringEventId]) continue;
        if (!byMaster[e.recurringEventId]) byMaster[e.recurringEventId] = [];
        byMaster[e.recurringEventId].push(e);
      }

      const rows = Object.entries(byMaster).map(([masterId, instances]) => {
        const sample = [...instances].sort((a, b) =>
          (a.start?.dateTime || a.start?.date || '').localeCompare(b.start?.dateTime || b.start?.date || '')
        )[0];
        const meta = parseFOHMeta(sample.description);
        const time = sample.start?.dateTime
          ? new Date(sample.start.dateTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
          : 'All day';
        const dow = sample.start?.dateTime
          ? new Date(sample.start.dateTime).toLocaleDateString('en-US', { weekday: 'short' })
          : '';
        return {
          recurringEventId: masterId,
          calendarId: sample._calendarId,
          title: stripAssigneeTag(sample.summary || '(No title)'),
          cadence: dow ? `${dow} · ${time}` : time,
          assignee: meta?.assignee || null,
          isReminder: meta?.reminder === true,
          // A series on a read-only/subscribed calendar (school calendar) —
          // Grove has no write access, so only Ignore is offered for it.
          // Prefer the calendar's authoritative accessRole; fall back to the
          // per-event organizer.self heuristic when meta lacks accessRole.
          isReadOnly: isReadOnlyCalendar(sample._calendarId, sample),
        };
      }).sort((a, b) => a.title.localeCompare(b.title));

      setSeries(rows);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function assignSeries(row, code) {
    if (!familyData?.id) return;
    setSavingId(row.recurringEventId);
    setActionError(null);
    try {
      const description = buildFOHDescription('', { assignee: code, reminder: false, reviewed: true });
      const newTitle = code ? `${row.title} (${code})` : row.title;

      await patchEvent(row.calendarId, row.recurringEventId, { summary: newTitle, description });

      await fsLogChange(familyData.id, {
        actorId: currentUser?.email, actorName: currentUser?.name,
        module: 'logistics', action: 'assign_series', entityType: 'event', entityId: row.recurringEventId,
        fromValue: row.assignee, toValue: `${code} — ${row.title}`,
      });

      setSeries(prev => prev.map(r => r.recurringEventId === row.recurringEventId ? { ...r, assignee: code, isReminder: false } : r));
      scheduleBatchedPublish(familyData);
    } catch (err) {
      console.error('[RecurringDefaults] assign series failed:', err);
      setActionError(`Couldn't assign "${row.title}": ${err.message}`);
    }
    setSavingId(null);
  }

  // Marks a series as a reminder — informational only, no assignee needed,
  // stays fully visible everywhere (never faded or hidden). Tap again to clear.
  async function toggleReminder(row) {
    if (!familyData?.id) return;
    setSavingId(row.recurringEventId);
    setActionError(null);
    try {
      const nextReminder = !row.isReminder;
      const description = buildFOHDescription('', {
        assignee: nextReminder ? null : row.assignee,
        reminder: nextReminder,
        reviewed: true,
      });

      await patchEvent(row.calendarId, row.recurringEventId, { summary: row.title, description });

      await fsLogChange(familyData.id, {
        actorId: currentUser?.email, actorName: currentUser?.name,
        module: 'logistics', action: nextReminder ? 'reminder_series' : 'unreminder_series',
        entityType: 'event', entityId: row.recurringEventId,
        toValue: `${nextReminder ? 'reminder' : 'active'} — ${row.title}`,
      });

      setSeries(prev => prev.map(r => r.recurringEventId === row.recurringEventId
        ? { ...r, isReminder: nextReminder, assignee: nextReminder ? null : r.assignee }
        : r));
      scheduleBatchedPublish(familyData);
    } catch (err) {
      console.error('[RecurringDefaults] toggle reminder failed:', err);
      setActionError(`Couldn't update reminder for "${row.title}": ${err.message}`);
    }
    setSavingId(null);
  }

  // Hides a series locally — never writes to the calendar. Works for both
  // owned and read-only calendars; the only option for read-only ones.
  async function ignoreSeries(row) {
    if (!familyData?.id) return;
    setSavingId(row.recurringEventId);
    setActionError(null);
    try {
      await fsSet(`families/${familyData.id}/ignoredEvents/${row.recurringEventId}`, {
        targetId: row.recurringEventId, scope: 'series', calendarId: row.calendarId, title: row.title,
        actorId: currentUser?.email, actorName: currentUser?.name,
        createdAt: new Date().toISOString(),
      });
      await fsLogChange(familyData.id, {
        actorId: currentUser?.email, actorName: currentUser?.name,
        module: 'logistics', action: 'ignore_event',
        entityType: 'event', entityId: row.recurringEventId,
        toValue: `series: ${row.title}`,
      });
      setSeries(prev => prev.filter(r => r.recurringEventId !== row.recurringEventId));
      scheduleBatchedPublish(familyData);
    } catch (err) {
      console.error('[RecurringDefaults] ignore series failed:', err);
      setActionError(`Couldn't hide "${row.title}": ${err.message}`);
    }
    setSavingId(null);
  }

  // Un-hide a previously ignored event/series. Deleting the doc lets the
  // event flow back into Review & Assign (and this list, if it's in-window).
  async function unignore(entry) {
    if (!familyData?.id) return;
    setSavingId(entry.id);
    setActionError(null);
    try {
      await fsDelete(`families/${familyData.id}/ignoredEvents/${entry.id}`);
      await fsLogChange(familyData.id, {
        actorId: currentUser?.email, actorName: currentUser?.name,
        module: 'logistics', action: 'unignore_event',
        entityType: 'event', entityId: entry.id,
        fromValue: `${entry.scope || 'instance'}: ${entry.title || entry.id}`,
      });
      setIgnoredList(prev => prev.filter(i => i.id !== entry.id));
      scheduleBatchedPublish(familyData);
      load(); // series may reappear if it falls within the window
    } catch (err) {
      console.error('[RecurringDefaults] unignore failed:', err);
      setActionError(`Couldn't un-hide "${entry.title || entry.id}": ${err.message}`);
    }
    setSavingId(null);
  }

  // Bulk title cleanup — writable, non-reminder series only (nothing to write
  // for a read-only calendar; a reminder's title is already clean/tag-free).
  const cleanupCandidates = series
    .filter(row => !row.isReadOnly && !row.isReminder)
    .map(row => ({ row, newTitle: standardizeTitle(row.title, { carePartners, children, sitters: allSitters, assigneeCode: row.assignee }) }))
    .filter(({ row, newTitle }) => newTitle !== row.title);

  const cleanupTitleById = new Map(cleanupCandidates.map(({ row, newTitle }) => [row.recurringEventId, newTitle]));

  // Per-item title fix — same write path as applyCleanup, scoped to one row.
  async function fixTitle(row, newTitle) {
    if (!familyData?.id) return;
    setSavingId(row.recurringEventId);
    setActionError(null);
    try {
      const resolvedCode = getEventAssignee({ summary: newTitle }) || row.assignee;
      const description = buildFOHDescription('', { assignee: resolvedCode, reminder: false, reviewed: true });
      await patchEvent(row.calendarId, row.recurringEventId, { summary: newTitle, description });
      await fsLogChange(familyData.id, {
        actorId: currentUser?.email, actorName: currentUser?.name,
        module: 'logistics', action: 'standardize_title',
        entityType: 'event', entityId: row.recurringEventId,
        fromValue: row.title, toValue: newTitle,
      });
      setSeries(prev => prev.map(r => r.recurringEventId === row.recurringEventId ? { ...r, title: newTitle } : r));
    } catch (err) {
      console.error('[RecurringDefaults] fix title failed:', err);
      setActionError(`Couldn't standardize "${row.title}": ${err.message}`);
    }
    setSavingId(null);
  }

  async function applyCleanup() {
    setApplyingCleanup(true);
    // Per-item results so a series that fails every run (which reads as the
    // count being "stuck") shows its actual reason instead of vanishing into a
    // bare failed count. Every candidate is attempted; one failure never stops
    // the rest.
    const results = [];
    for (const { row, newTitle } of cleanupCandidates) {
      if (row.isReadOnly) {
        results.push({ title: row.title, newTitle, status: 'skipped', reason: 'read-only calendar' });
        continue;
      }
      try {
        // Read the (possibly-repaired) assignee code back off the cleaned
        // title, so a leaked raw sitter id gets fixed in the FOH metadata too.
        const resolvedCode = getEventAssignee({ summary: newTitle }) || row.assignee;
        const description = buildFOHDescription('', { assignee: resolvedCode, reminder: false, reviewed: true });
        await patchEvent(row.calendarId, row.recurringEventId, { summary: newTitle, description });
        results.push({ title: row.title, newTitle, status: 'updated' });
      } catch (err) {
        console.error('[RecurringDefaults] title cleanup failed for', row.recurringEventId, err);
        results.push({ title: row.title, newTitle, status: 'failed', reason: err.message });
      }
    }
    const applied = results.filter(r => r.status === 'updated').length;
    if (familyData?.id && applied > 0) {
      await fsLogChange(familyData.id, {
        actorId: currentUser?.email, actorName: currentUser?.name,
        module: 'logistics', action: 'standardize_titles',
        toValue: `${applied} title(s) cleaned up`,
      });
    }
    setApplyingCleanup(false);
    setShowCleanupPreview(false);
    setCleanupResults(results);
    if (applied > 0) load();
  }

  // Family-wide pickup season toggle — suppresses pickups everywhere (e.g.
  // summer break) without touching any child's individual configuration.
  const pickupsActive = familyData?.config?.pickupsActive !== false;
  async function togglePickupsActive() {
    if (!familyData?.id) return;
    const next = !pickupsActive;
    await fsSet(`families/${familyData.id}/config/appConfig`, { pickupsActive: next });
    await fsLogChange(familyData.id, {
      actorId: currentUser?.email, actorName: currentUser?.name,
      module: 'logistics', action: 'toggle_pickups_active', toValue: next ? 'active' : 'paused',
    });
    setFamilyData(prev => ({ ...prev, config: { ...prev.config, pickupsActive: next } }));
  }

  // Pickup defaults — read-only summary; edited in Settings → Family Setup → Children.
  // One card per enabled leg (drop-off / pickup) of each routine on the child.
  const pickupRows = children.map(child => {
    const cards = [];
    for (const routine of getRoutines(child)) {
      const days = routine.days?.length ? routine.days : ['Mo', 'Tu', 'We', 'Th', 'Fr'];
      for (const leg of ['dropoff', 'pickup']) {
        const legData = routine[leg];
        if (!legData || legData.enabled === false) continue;
        const perDay = days.map(d => ({ day: d, adultId: legAdultForDay(legData, d) }));
        const label = leg === 'dropoff'
          ? `${routine.label} drop-off`
          : (routine._legacyLabel || `${routine.label} pickup`);
        cards.push({ key: `${routine.id}_${leg}`, leg, label, perDay, hasUnset: perDay.some(p => !p.adultId) });
      }
    }
    return { child, cards };
  });

  const body = (
    <>
        <p className="settings-intro settings-intro--strong">
          Manage your recurring calendar events and weekly pickup routines here, so Review & Assign can stay focused on day-to-day exceptions.
        </p>

        {actionError && (
          <div className="error-banner">
            🌧️ {actionError}
            <button className="btn btn--link" onClick={() => setActionError(null)}>✕</button>
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 12 }}>
          <h3 className="registry-section__title" style={{ margin: 0 }}>
            <span className="recurring-glyph recurring-glyph--lead" aria-hidden="true"><RecurringIcon /></span>Recurring events
          </h3>
          {!loading && !error && cleanupCandidates.length > 0 && (
            <button className="btn btn--secondary btn--small" onClick={() => { setShowCleanupPreview(true); setCleanupResults(null); }}>
              🧹 Standardize titles ({cleanupCandidates.length})
            </button>
          )}
        </div>
        {cleanupResults && (
          <div className="pickup-action-strip">
            <p className="settings-note" style={{ marginTop: 0 }}>
              {cleanupResults.filter(r => r.status === 'updated').length} updated
              {cleanupResults.some(r => r.status === 'skipped') && `, ${cleanupResults.filter(r => r.status === 'skipped').length} skipped`}
              {cleanupResults.some(r => r.status === 'failed') && `, ${cleanupResults.filter(r => r.status === 'failed').length} failed`}.
            </p>
            {cleanupResults.map((r, i) => (
              <div key={i} className={`cleanup-result cleanup-result--${r.status}`}>
                {r.status === 'updated' ? '✓' : r.status === 'skipped' ? '–' : '⚠'}{' '}
                <span style={{ opacity: 0.7 }}>{r.title}</span> → <strong>{r.newTitle}</strong>
                {r.reason && ` (${r.reason})`}
              </div>
            ))}
            <div style={{ marginTop: 8 }}>
              <button className="btn btn--ghost btn--small" onClick={() => setCleanupResults(null)}>Dismiss</button>
            </div>
          </div>
        )}
        {showCleanupPreview && (
          <div className="pickup-action-strip">
            <p className="settings-note" style={{ marginTop: 0 }}>
              Preview — {cleanupCandidates.length} title{cleanupCandidates.length !== 1 ? 's' : ''} will change. Skips read-only calendars and reminders.
            </p>
            {cleanupCandidates.map(({ row, newTitle }) => (
              <div key={row.recurringEventId} className="settings-note" style={{ margin: '4px 0' }}>
                <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>{row.title}</span>
                {' → '}
                <strong>{newTitle}</strong>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button className="btn btn--primary btn--small" disabled={applyingCleanup} onClick={applyCleanup}>
                {applyingCleanup ? 'Applying…' : `Apply ${cleanupCandidates.length} change${cleanupCandidates.length !== 1 ? 's' : ''}`}
              </button>
              <button className="btn btn--ghost btn--small" disabled={applyingCleanup} onClick={() => setShowCleanupPreview(false)}>Cancel</button>
            </div>
          </div>
        )}
        {loading && <p className="placeholder-text">Loading…</p>}
        {error && <div className="error-banner">🌧️ {error}</div>}
        {!loading && !error && series.length === 0 && (
          <p className="empty-state">No recurring events found in the next {WINDOW_DAYS} days.</p>
        )}
        {!loading && !error && series.map(row => (
          <div key={row.recurringEventId} className="pickup-card">
            <div className="pickup-card__top">
              <span className="pickup-card__label">
                <span className="recurring-glyph recurring-glyph--lead" aria-hidden="true"><RecurringIcon /></span>
                {row.title} · {row.cadence}
                {row.isReadOnly && <span className="readonly-mark" title="External calendar — read-only" aria-label="External read-only calendar">🔒</span>}
              </span>
              {row.isReminder && <span className="badge badge--reminder"><ReminderIcon /> Reminder</span>}
              {!row.isReadOnly && !row.isReminder && !row.assignee && <span className="badge badge--unassigned">unset</span>}
            </div>
            <div className="pickup-card__actions">
              {row.isReadOnly ? (
                <button className="assignee-btn assignee-btn--cancel"
                  disabled={savingId === row.recurringEventId}
                  onClick={() => ignoreSeries(row)}
                  title="Hide this series in Grove — doesn't touch the calendar"
                  aria-label="Ignore — hide this series in Grove"
                >🙈</button>
              ) : (
                <>
                  {!row.isReminder && carePartners.map(cp => (
                    <button key={cp.identifier}
                      className={`assignee-btn ${row.assignee === cp.identifier ? 'assignee-btn--active' : ''}`}
                      disabled={savingId === row.recurringEventId}
                      onClick={() => assignSeries(row, cp.identifier)}
                    >({cp.identifier})</button>
                  ))}
                  {!row.isReminder && familySitters.map(fs => (
                    <button key={fs.identifier}
                      className={`assignee-btn assignee-btn--family ${row.assignee === fs.identifier ? 'assignee-btn--active' : ''}`}
                      disabled={savingId === row.recurringEventId}
                      onClick={() => assignSeries(row, fs.identifier)}
                    >({fs.identifier})</button>
                  ))}
                  {!row.isReminder && cleanupTitleById.has(row.recurringEventId) && (
                    <button
                      className="assignee-btn assignee-btn--fix-title"
                      disabled={savingId === row.recurringEventId}
                      onClick={() => fixTitle(row, cleanupTitleById.get(row.recurringEventId))}
                      title={`Standardize title to "${cleanupTitleById.get(row.recurringEventId)}"`}
                      aria-label="Standardize this title"
                    >✓ Fix title</button>
                  )}
                  <button
                    className={`assignee-btn ${row.isReminder ? 'assignee-btn--reminder-active' : 'assignee-btn--reminder'}`}
                    disabled={savingId === row.recurringEventId}
                    onClick={() => toggleReminder(row)}
                    title={row.isReminder ? 'Clear reminder — this series will need an assignee again' : 'Mark as a reminder — informational only, no assignee needed'}
                    aria-label={row.isReminder ? 'Clear reminder' : 'Mark as a reminder'}
                  ><ReminderIcon /></button>
                </>
              )}
            </div>
          </div>
        ))}

        {!loading && ignoredList.length > 0 && (
          <>
            <h3 className="registry-section__title" style={{ marginTop: 24, marginBottom: 12 }}>🙈 Ignored events</h3>
            <p className="settings-note" style={{ marginTop: 0, marginBottom: 8 }}>
              Hidden from Review & Assign and Home — nothing was written to the calendar. Un-hide to bring one back.
            </p>
            {ignoredList.map(entry => (
              <div key={entry.id} className="ignore-row">
                <div className="ignore-row__info">
                  <span className="ignore-row__title">{entry.title || '(untitled)'}</span>
                  <span className="ignore-row__meta">
                    {entry.scope === 'series' ? 'Series' : 'Single event'}
                    {calMetaById[entry.calendarId]?.name && ` · ${calMetaById[entry.calendarId].name}`}
                    {entry.actorName && ` · by ${entry.actorName}`}
                    {entry.createdAt && ` · ${new Date(entry.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                  </span>
                </div>
                <button className="btn btn--ghost btn--small ignore-row__action"
                  disabled={savingId === entry.id}
                  onClick={() => unignore(entry)}
                >Un-hide</button>
              </div>
            ))}
          </>
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 24 }}>
          <h3 className="registry-section__title" style={{ margin: 0 }}>🚗 Pickup defaults</h3>
          <button className={`btn btn--small ${pickupsActive ? 'btn--secondary' : 'btn--primary'}`} onClick={togglePickupsActive}>
            {pickupsActive ? 'Pickups active this season' : 'Pickups paused — tap to resume'}
          </button>
        </div>
        {!pickupsActive && (
          <p className="settings-note">
            Paused — no unset-pickup badges will show anywhere in the app. Nothing below was changed; flip this back on when the school year resumes.
          </p>
        )}
        {pickupRows.length === 0 && <p className="empty-state">No children added yet.</p>}
        {pickupRows.map(({ child, cards }) => (
          <div key={child.id} style={{ marginBottom: 10 }}>
            <span className="pickup-card__label" style={{ display: 'block', marginBottom: 4 }}>
              {child.preferred_name || child.firstName}
            </span>
            {cards.length === 0 ? (
              <p className="settings-note" style={{ margin: 0 }}>No routines set.</p>
            ) : cards.map(card => (
              <div key={card.key} className="pickup-card" style={pickupsActive ? undefined : { opacity: 0.55 }}>
                <div className="pickup-card__top">
                  <span className="pickup-card__label">
                    {card.leg === 'dropoff' ? '🎒' : '🚗'} {card.label}
                  </span>
                  {pickupsActive && card.hasUnset && <span className="badge badge--unassigned">unset</span>}
                </div>
                <p className="settings-note" style={{ margin: 0 }}>
                  {card.perDay.map(p => `${p.day} ${p.adultId ? resolvePickupAdult(p.adultId, { carePartners, sitters: familyData?.sitters || [] }) : '—'}`).join('  ·  ')}
                </p>
              </div>
            ))}
          </div>
        ))}
        {onNavigateToChildren ? (
          <button className="btn btn--link" style={{ marginTop: 8, padding: 0 }} onClick={onNavigateToChildren}>
            Edit weekly patterns in Family Setup → Children →
          </button>
        ) : (
          <p className="settings-note" style={{ marginTop: 4 }}>Edit weekly patterns in Settings → Family Setup → Children.</p>
        )}
    </>
  );

  // Rendered as a tab pane inside Settings → Calendars, whose SettingsGroup wrapper
  // already provides the module-screen/header/settings-body. The Logistics Review & Assign
  // entry point renders it full-screen instead.
  if (embedded) {
    return body;
  }

  return (
    <div className="module-screen">
      <div className="module-header">
        <button className="btn btn--back" onClick={onBack}>← Back</button>
        <h1>Recurring Events &amp; Defaults</h1>
      </div>
      <div className="settings-body">{body}</div>
    </div>
  );
}
